/**
 * 版本检查工具
 * 定期检测应用版本更新
 */

import { useUserStore } from '@/stores/user'

// 版本信息接口
interface VersionData {
  version: string
  [key: string]: any // 允许其他额外字段
}

// 当前应用版本（从环境变量读取）
const CURRENT_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0.0'

// 🔧 保存定时器 ID，防止内存泄漏
let versionCheckTimer: NodeJS.Timeout | null = null
let initialCheckTimer: NodeJS.Timeout | null = null

/**
 * 停止版本检查
 */
export function stopVersionCheck(): void {
  if (versionCheckTimer) {
    clearInterval(versionCheckTimer)
    versionCheckTimer = null
    console.log('✅ 版本检查定时器已清理')
  }
  if (initialCheckTimer) {
    clearTimeout(initialCheckTimer)
    initialCheckTimer = null
    console.log('✅ 初始版本检查定时器已清理')
  }
}

/**
 * 启动版本检查
 * @param intervalMinutes 检查间隔（分钟），默认5分钟
 */
export function startVersionCheck(intervalMinutes: number = 1): void {
  // 🔧 先清理旧的定时器（防止重复调用导致累积）
  stopVersionCheck()
  
  const intervalMs = intervalMinutes * 60 * 1000 // 转换为毫秒

  /**
   * 执行版本检查
   */
  async function checkVersion(): Promise<void> {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
      const data: VersionData = await res.json()

      console.log(`📦 当前版本: ${CURRENT_VERSION}, 服务器版本: ${data.version}`)

      if (data.version && data.version !== CURRENT_VERSION) {
        console.log('🔄 检测到新版本，触发更新提示')
        showUpdateDialog(data)
      }
    } catch (e) {
      console.error('❌ 版本检测失败:', e)
    }
  }

  // 初次延迟3秒后检测
  initialCheckTimer = setTimeout(() => {
    checkVersion()
    initialCheckTimer = null // 清除引用
  }, 3000)

  // 后续定时检测
  versionCheckTimer = setInterval(checkVersion, intervalMs)

  console.log(`✅ 版本检查已启动，间隔: ${intervalMinutes} 分钟`)
  
  // 🔧 页面卸载时清理定时器
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', stopVersionCheck)
  }
}

/**
 * 显示更新提示弹窗
 * @param _data 版本数据（暂未使用，保留以供未来扩展）
 */
function showUpdateDialog(_data: VersionData): void {
  try {
    // 在函数内部获取 store 实例，确保 Pinia 已经初始化
    const userStore = useUserStore()
    userStore.setIsVersionUpdate(true)
    console.log('✅ 版本更新提示已触发')
  } catch (error) {
    console.error('❌ 显示更新提示失败:', error)
  }
}

